/**
 * Форматирует дату в относительном формате (русский язык).
 *
 * @param {string|number|Date} dateStr — дата
 * @param {Object} [opts]
 * @param {boolean} [opts.fallbackFull=false] — если true, для старых дат (>30 дней)
 *        возвращать полную дату с временем, иначе — короткую
 * @returns {string}
 *
 * @example
 * formatRelativeTime(new Date())           // "только что"
 * formatRelativeTime(5 min ago)            // "5 мин. назад"
 * formatRelativeTime(3 hours ago)          // "3 ч. назад"
 * formatRelativeTime(yesterday)            // "вчера"
 * formatRelativeTime(5 days ago)           // "5 дн. назад"
 * formatRelativeTime(2 months ago)         // "12.05.2026"  (короткая)
 * formatRelativeTime(2 months ago, {fallbackFull:true}) // "12.05.2026, 14:30"
 */
export function formatRelativeTime(dateStr, opts = {}) {
    if (!dateStr) return '';
    const { fallbackFull = false } = opts;

    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    // Будущее (небольшой запас для рассинхрона часов)
    if (diffSec < 0 && diffSec > -60) return 'только что';

    if (diffSec < 60) return 'только что';
    if (diffMin < 60) {
        const word = pluralRu(diffMin, 'минуту', 'минуты', 'минут');
        return `${diffMin} ${word} назад`;
    }
    if (diffHour < 24) {
        const word = pluralRu(diffHour, 'час', 'часа', 'часов');
        return `${diffHour} ${word} назад`;
    }
    if (diffDay === 1) return 'вчера';
    if (diffDay < 7) {
        const word = pluralRu(diffDay, 'день', 'дня', 'дней');
        return `${diffDay} ${word} назад`;
    }
    if (diffDay < 30) {
        const diffWeek = Math.floor(diffDay / 7);
        const word = pluralRu(diffWeek, 'неделю', 'недели', 'недель');
        return `${diffWeek} ${word} назад`;
    }

    // Старше 30 дней — абсолютная дата
    if (fallbackFull) {
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

/**
 * Русская плюрализация
 * @param {number} n
 * @param {string} one — форма для 1
 * @param {string} few — форма для 2-4
 * @param {string} many — форма для 5+
 */
function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}